<template>
	<div v-if="node">
		<ProfileSelectionMenu
			v-if="showProfileSelector && node.requires_auth"
			:serviceId="node.service"
			:configurationId="configurationId"
			:serviceVersionId="node.service_version"
			:profileId="node.credentials_configuration.credentials"
			:credentialsType="node.credentials_configuration.credentials_type"
			:sourceFieldUUID="node.credentials_configuration.source_field_uuid"
			:overrideSelectProfile="selectProfile"
			:hasEditPermissions="hasEditPermissions"
		/>
		<div v-for="param in params" :key="`node-${node.id}-param-${param.key}`">
			<ParamDispatcherDataProvider
				:param="param"
				:node="nodeProxy"
				:key="`node-${node.id}-param-${param.key}`"
				:workflowVersion="editedWorkflowVersion"
				@update-input-field="updateInputField"
				@update-input-field-label="updateInputFieldLabel"
			/>
		</div>
	</div>
</template>

<script>
import debounce from 'lodash/debounce';

import { CredentialsTypes } from '@ply-io/ply-front-shared-utilities';

import ParamDispatcherDataProvider from '@/components/BlockEditor/BlockEditorParams/ParamDispatcherDataProvider';
import ProfileSelectionMenu from '@/components/BlockEditor/IntegrationEditor/ProfileSelection/ProfileSelectionMenu';
import { getActionParams } from '@/store/modules/actions/getters';
import { getActionsOfApp } from '@/store/modules/apps/actions';
import { partialUpdateNode } from '@/store/modules/nodes/actions';
import { getNode } from '@/store/modules/nodes/getters';
import { updateNotification } from '@/store/modules/selectedNode/actions';
import { editedWorkflowVersion } from '@/store/modules/selectedWorkflow/getters';
import { userSpecificConfigurationId, generalConfigurationId } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { canEditWorkflow } from '@/store/modules/workflows/getters';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
import { VIRTUAL_PROFILES_IDS, EMBEDDED_CONNECTION_ID } from '@/utils/credentials-service.const';

const DEBOUNCE_TIME = 600;

export default {
	name: 'CompactNodeEditor',
	props: {
		node: { type: Object, required: true },
		showProfileSelector: { type: Boolean, default: false },
	},
	components: {
		ParamDispatcherDataProvider,
		ProfileSelectionMenu,
	},
	data: () => ({
		paramsMap: null,
		paramsMapLabels: null,
	}),
	computed: {
		...mapGettersShorthand({
			editedWorkflowVersion,
			userSpecificConfigurationId,
			generalConfigurationId,
			getNode,
			getActionParams,
			canEditWorkflow,
		}),
		configurationId() {
			return this.node?.credentials_configuration?.credentials_type === CredentialsTypes.ProfileBased
				? this.userSpecificConfigurationId
				: this.generalConfigurationId; // CredentialsTypes.AccountBased
		},
		params() {
			return this.getActionParams(this.node.action).filter(({ hidden }) => !hidden);
		},
		nodeProxy() {
			return { ...this.node, params_map: this.paramsMap, params_map_labels: this.paramsMapLabels };
		},
		hasEditPermissions() {
			return this.canEditWorkflow(this.node.workflow);
		},
	},
	methods: {
		...mapActionsShorthand({ updateNotification, partialUpdateNode, getActionsOfApp }),

		async updateInputField({ map: { key, value } }) {
			this.$set(this.paramsMap, key, value);
			this.debouncedUpdateParams();
		},
		updateInputFieldLabel({ label: { key, value } }) {
			this.$set(this.paramsMapLabels, key, value);
			this.debouncedUpdateParams();
		},
		selectProfile(credsId, sourceFieldUUID, credentialsType) {
			const newCredConfigData = {
				credentials: [...VIRTUAL_PROFILES_IDS, EMBEDDED_CONNECTION_ID].includes(credsId) ? null : credsId,
				credentials_type: VIRTUAL_PROFILES_IDS.includes(credsId) ? credentialsType : CredentialsTypes.Embedded,
			};
			if (sourceFieldUUID) {
				newCredConfigData.source_field_uuid = sourceFieldUUID;
			}
			this.partialUpdateNode({ id: this.node.id, data: { credentials_configuration: newCredConfigData } });
		},
	},
	created() {
		this.paramsMap = { ...(this.node.params_map || {}) };
		this.paramsMapLabels = { ...(this.node.params_map_labels || {}) };
		this.debouncedUpdateParams = debounce(
			() => this.partialUpdateNode({ id: this.node.id, data: { params_map: this.paramsMap, params_map_labels: this.paramsMapLabels } }),
			DEBOUNCE_TIME
		);
		this.getActionsOfApp({ id: this.node.service, version: this.node.service_version });
	},
};
</script>
